import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import AxisSpinner from "./AxisSpinner";
import DropDownTimesheetWeekSelection from "./DropDownTimesheetWeekSelection";
import { Alert, Button, Card, Col, Container, Row } from "react-bootstrap";
import { BsDownload, BsReceipt } from "react-icons/bs";

const csvColumns = [
  { key: "si", label: "SI" },
  { key: "sageCustomerRef", label: "Sage customer ref" },
  { key: "sageNominalLedgerCode", label: "sage nominal ledger code" },
  { key: "department", label: "Department (zero)" },
  { key: "date", label: "Date" },
  { key: "salesInvoiceNumber", label: "Sales invoice number" },
  { key: "details", label: "Details" },
  { key: "net", label: "Net" },
  { key: "vatCode", label: "Vat Code" },
  { key: "vat", label: "Vat" },
];

const SageInvoiceReportTable = () => {
  const { authAxios } = useAuth();
  const [timesheetWeek, setTimesheetWeek] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!timesheetWeek) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await authAxios().get(
        "/api/backoffice/reporting/sage_invoice_report",
        {
          params: {
            timesheet_week_id: timesheetWeek.id,
          },
        },
      );
      setRows(response.data || []);
    } catch (err) {
      console.log(err);
      setError("Failed to load Sage invoice report.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios, timesheetWeek]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatNet = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const number = Number(value);
    if (Number.isNaN(number)) return value;
    return number.toFixed(2);
  };

  const displayValue = (value) => {
    if (value === null || value === undefined) return "";
    return value;
  };

  const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const exportCsv = () => {
    const lines = [
      csvColumns.map((column) => escapeCsvValue(column.label)).join(","),
      ...rows.map((row) =>
        csvColumns
          .map((column) =>
            escapeCsvValue(column.key === "net" ? formatNet(row[column.key]) : row[column.key]),
          )
          .join(","),
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sage-invoice-report-${timesheetWeek?.weekEnding || "week"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const reportContent = () => {
    if (!timesheetWeek) {
      return <Alert variant="info">Select a timesheet week to load the report.</Alert>;
    }

    if (loading) {
      return (
        <div className="text-center py-5">
          <AxisSpinner loading={loading} />
        </div>
      );
    }

    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    if (rows.length === 0) {
      return <Alert variant="info">No sent invoices found for this week.</Alert>;
    }

    return (
      <div className="table-responsive">
        <Table striped bordered hover className="mb-0">
          <thead className="bg-light">
            <tr>
              {csvColumns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.salesInvoiceNumber}-${index}`}>
                <td>{displayValue(row.si)}</td>
                <td>{displayValue(row.sageCustomerRef)}</td>
                <td>{displayValue(row.sageNominalLedgerCode)}</td>
                <td>{displayValue(row.department)}</td>
                <td>{displayValue(row.date)}</td>
                <td>{displayValue(row.salesInvoiceNumber)}</td>
                <td>{displayValue(row.details)}</td>
                <td>{formatNet(row.net)}</td>
                <td>{displayValue(row.vatCode)}</td>
                <td>{displayValue(row.vat)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <BsReceipt className="me-2" />
                Sage Invoice Report
              </h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={exportCsv}
                disabled={rows.length === 0}
              >
                <BsDownload className="me-2" />
                Export CSV
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <DropDownTimesheetWeekSelection onSelected={setTimesheetWeek} />
            </Col>
          </Row>
          {reportContent()}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SageInvoiceReportTable;
